/**
 * Domain resources.
 *
 * The five standard operations come from `createResource`. Only the calls that are
 * genuinely domain-specific — renew a policy, reconcile a statement, change a claim
 * status — are written out, and each is a few lines because the client and cache-key
 * conventions are already established.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityDto,
  AgentPerformanceRow,
  AuditLogDto,
  BreakdownRow,
  CancellationPreviewDto,
  ClaimDto,
  ClaimTimelineDto,
  ClientDto,
  DashboardQuery,
  DashboardSummaryDto,
  DocumentDto,
  EndorsementDto,
  ImportResultDto,
  InstallmentDto,
  InsuranceCompanyDto,
  InsurerCommissionRateDto,
  InsurerCommissionRateInput,
  NotificationDto,
  PolicyDto,
  PolicyRenewalDto,
  RenewalEmailBatchDto,
  RenewalEmailPreviewDto,
  RenewalEmailSettingsDto,
  RenewalPreviewDto,
  SearchResultsDto,
  SendRenewalEmailResultDto,
  SettlementDto,
  TimeSeriesPoint,
  TodayDto,
  UpdateRenewalEmailSettingsInput,
  UserDto,
} from '@ias/shared';

import { getAll, getList, getOne, patch, post, put, remove, upload } from './client';
import { createResource, queryKeys } from './resource';

/* ── standard resources ─────────────────────────────────────────────────────── */

export const clientsApi = createResource<ClientDto, Record<string, unknown>>('clients');
export const policiesApi = createResource<PolicyDto, Record<string, unknown>>('policies');
export const claimsApi = createResource<ClaimDto, Record<string, unknown>>('claims');
export const remindersApi = createResource<unknown, Record<string, unknown>>('reminders');
export const settlementsApi = createResource<SettlementDto, Record<string, unknown>>(
  'settlements',
  'commissions/settlements',
);
export const auditApi = createResource<AuditLogDto, Record<string, unknown>>('audit');

/* ── reference data ─────────────────────────────────────────────────────────── */

export function useInsuranceCompanies(includeInactive = false) {
  return useQuery({
    queryKey: queryKeys.custom('insurance-companies', 'all', { includeInactive }),
    queryFn: () => getAll<InsuranceCompanyDto>('/insurance-companies', { includeInactive }),
    // Reference data changes rarely; a long stale time keeps every form's insurer
    // dropdown instant without a refetch per mount.
    staleTime: 10 * 60_000,
  });
}

/**
 * Replaces a carrier's per-category commission grid.
 *
 * Invalidates the insurer list rather than a rates-specific key, because the rates travel on
 * the carrier: that list is what the policy form reads to pre-fill a rate, and leaving it
 * cached would keep pre-filling yesterday's percentage.
 */
export function useSaveInsurerCommissionRates() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, rates }: { companyId: string; rates: InsurerCommissionRateInput[] }) =>
      put<InsurerCommissionRateDto[]>(`/insurance-companies/${companyId}/commission-rates`, { rates }),
    onSuccess: () => {
      // The resource prefix, not `custom(...)`: the list key carries an `{ includeInactive }`
      // params object, and a three-segment key with an empty object would not match it.
      client.invalidateQueries({ queryKey: queryKeys.resource('insurance-companies') });
    },
  });
}

export function useAssignableUsers() {
  return useQuery({
    queryKey: queryKeys.custom('users', 'assignable'),
    queryFn: () => getAll<Pick<UserDto, 'id' | 'fullName' | 'role'>>('/users/assignable'),
    staleTime: 10 * 60_000,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: queryKeys.custom('users', 'all'),
    queryFn: () => getAll<UserDto>('/users'),
  });
}

export function useClientCities() {
  return useQuery({
    queryKey: queryKeys.custom('clients', 'cities'),
    queryFn: () => getAll<string>('/clients/cities'),
    staleTime: 5 * 60_000,
  });
}

export function useClientTags() {
  return useQuery({
    queryKey: queryKeys.custom('clients', 'tags'),
    queryFn: () => getAll<string>('/clients/tags'),
    staleTime: 5 * 60_000,
  });
}

/* ── policies: renewal cycle ────────────────────────────────────────────────── */

/**
 * `overrides` are the operator's edits in the renewal dialog, echoed back through the server so
 * the commission beside them is the figure that will actually be booked — recomputing it in the
 * browser would eventually disagree with the engine over rounding or the καθαρά rule.
 *
 * They are part of the query key, so each distinct set of edits is its own cache entry and the
 * figures cannot lag a keystroke behind the inputs that produced them.
 */
export function useRenewalPreview(
  policyId: string | undefined,
  enabled = true,
  overrides?: Record<string, string | undefined>,
) {
  const params = Object.fromEntries(
    Object.entries(overrides ?? {}).filter(([, value]) => value !== undefined && value !== ''),
  );

  return useQuery({
    queryKey: queryKeys.sub('policies', policyId ?? '', 'renewal-preview', params),
    queryFn: () => getOne<RenewalPreviewDto>(`/policies/${policyId}/renewal-preview`, params),
    enabled: Boolean(policyId) && enabled,
    /**
     * Keep the preview on screen while an edit is re-priced.
     *
     * The overrides are part of the key, so every settled edit starts a *new* query — which
     * is pending, which made `isLoading` true, which swapped the whole next-period card for a
     * skeleton and back. On a fast machine that is a flicker. On the office's Windows 7 box it
     * meant the card vanished mid-typing, and because the inputs were unmounted and remounted
     * the caret went with them: «σβήνω ένα νούμερο, ξανακάνω κλικ, σβήνω το άλλο». The figures
     * that are being recomputed are already dimmed while `isFetching`, which is the honest
     * signal; blanking the form was never one.
     *
     * Bounded to the same policy: carrying one policy's premium into another policy's dialog
     * is the one thing worse than a skeleton.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === policyId ? previous : undefined,
    // Always re-fetched when the modal opens: a stale preview would promise a period
    // the server no longer intends to write.
    staleTime: 0,
    gcTime: 0,
  });
}

export interface MarkPaidInput {
  policyId: string;
  paymentMethod?: string;
  paymentDate?: string;
  notes?: string;
  nextPremiumAmount?: string;
  /** And its καθαρά — repricing only the gross would leave the commission on last year's base. */
  nextNetPremiumAmount?: string;
  nextCommissionPercentage?: string;
  /** Αριθμός ανανεωτηρίου for the period being opened; null when the insurer issues none. */
  nextRenewalNumber?: string | null;
  /** The period actually issued, when it is not the computed one. Omitted means «as computed». */
  nextStartDate?: string;
  nextEndDate?: string;
  /** A new frequency for the period being opened; omitted means «unchanged». */
  nextRenewalFrequency?: string;
  /** Idempotency key — one per intent to collect, so a retry cannot renew twice. */
  clientRef?: string;
  /** The period on screen; the server refuses a renewal that finds the policy already past it. */
  expectedPeriodNumber?: number;
}

export function useMarkPolicyPaid() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, ...body }: MarkPaidInput) =>
      patch<{ policy: PolicyDto; renewed: boolean; reason?: string }>(
        `/policies/${policyId}/payment-status`,
        { paymentStatus: 'paid', ...body },
      ),
    onSuccess: () => {
      // A renewal moves money and dates — dashboard, reminders and the daily
      // worklist all go stale together.
      client.invalidateQueries({ queryKey: queryKeys.resource('policies') });
      client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
      client.invalidateQueries({ queryKey: queryKeys.resource('reminders') });
      client.invalidateQueries({ queryKey: queryKeys.resource('today') });
    },
  });
}

/**
 * Records the ειδοποιητήριο call.
 *
 * Invalidates the dashboard too, since D5: «Δεν ανανεώνει» removes the policy from the
 * upcoming-renewals quick list, the KPI and the workload forecast, so the notice call now
 * DOES change dashboard numbers. (It once deliberately skipped the dashboard — with the
 * 30s staleTime, that left a declined policy sitting in «Ανανεώσεις επόμενων 30 ημερών»
 * until something else happened to refetch it.)
 */
export function useUpdateRenewalNotice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      policyId,
      ...body
    }: {
      policyId: string;
      noticeStatus: string;
      noticeReceivedAt?: string | null;
      noticeNote?: string | null;
    }) => patch<PolicyDto>(`/policies/${policyId}/notice`, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.resource('policies') });
      client.invalidateQueries({ queryKey: queryKeys.resource('today') });
      client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
    },
  });
}

/* ── ειδοποιητήριο by email ─────────────────────────────────────────────────── */

/**
 * The agency's renewal-email settings.
 *
 * Readable by anyone who can read the book, because the worklist has to know whether the feature
 * is on before it offers a send button; only an admin can write them.
 */
export function useRenewalEmailSettings() {
  return useQuery({
    queryKey: queryKeys.custom('renewal-email', 'settings'),
    queryFn: () => getOne<RenewalEmailSettingsDto>('/settings/renewal-email'),
    staleTime: 60_000,
  });
}

export function useSaveRenewalEmailSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateRenewalEmailSettingsInput) =>
      put<RenewalEmailSettingsDto>('/settings/renewal-email', body),
    onSuccess: (settings) => {
      // Seeded rather than invalidated: the response *is* the new settings, and a refetch here
      // would blank the form it was just saved from for a frame.
      client.setQueryData(queryKeys.custom('renewal-email', 'settings'), settings);
    },
  });
}

export function useSendTestRenewalEmail() {
  return useMutation({
    mutationFn: (recipient: string) =>
      post<SendRenewalEmailResultDto>('/settings/renewal-email/test', { recipient }),
  });
}

/** The rendered notice for one policy — what the send dialog shows before anything is sent. */
export function useRenewalEmailPreview(policyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub('policies', policyId ?? '', 'renewal-email'),
    queryFn: () => getOne<RenewalEmailPreviewDto>(`/policies/${policyId}/renewal-email`),
    enabled: Boolean(policyId),
    // Always re-rendered: the premium or the client's address may have changed since the dialog
    // was last opened, and this is the copy about to reach a client.
    staleTime: 0,
  });
}

export function useSendRenewalEmail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) =>
      post<SendRenewalEmailResultDto>(`/policies/${policyId}/renewal-email`, {}),
    onSuccess: () => invalidateNoticeViews(client),
  });
}

/** The bulk bar's action, and the admin's «στείλε τα εκκρεμή» — same cache consequences. */
export function useSendRenewalEmails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (policyIds: string[]) =>
      post<RenewalEmailBatchDto>('/policies/renewal-email/send', { policyIds }),
    onSuccess: () => invalidateNoticeViews(client),
  });
}

export function useSendDueRenewalEmails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (windowDays?: number) =>
      post<RenewalEmailBatchDto>('/policies/renewal-email/send-due', { windowDays }),
    onSuccess: () => invalidateNoticeViews(client),
  });
}

/**
 * A sent notice moves the policy's notice status as well as its sent stamp, which is the same
 * set of screens the call dialog disturbs — the worklist, the day list and the dashboard's
 * renewal figures all read one or the other.
 */
function invalidateNoticeViews(client: ReturnType<typeof useQueryClient>) {
  client.invalidateQueries({ queryKey: queryKeys.resource('policies') });
  client.invalidateQueries({ queryKey: queryKeys.resource('today') });
  client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
}

export function useCancelPolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, reason, effectiveDate }: { policyId: string; reason: string; effectiveDate?: string }) =>
      patch<PolicyDto>(`/policies/${policyId}/cancel`, { reason, effectiveDate }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.resource('policies') });
      client.invalidateQueries({ queryKey: queryKeys.resource('today') });
    },
  });
}

/**
 * Server-computed ακυρωτικό. Fetched fresh every open (staleTime 0) because the
 * refund shown must be the refund that will be stored — never a cached one.
 */
export function useCancellationPreview(
  policyId: string | undefined,
  effectiveDate: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.sub('policies', policyId ?? '', 'cancellation-preview', { effectiveDate }),
    queryFn: () =>
      getOne<CancellationPreviewDto>(
        `/policies/${policyId}/cancellation-preview`,
        effectiveDate ? { effectiveDate } : undefined,
      ),
    enabled: Boolean(policyId) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useBulkPolicyAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      policyIds: string[];
      action: string;
      reason?: string;
      assignedAgentId?: string;
      /**
       * Supplied by the caller and stable for as long as the selection is, so two
       * submissions of the same batch collapse into one. `mark_paid` runs the renewal
       * transition once per policy; without a key a double-click renews every selected
       * policy twice. Minting it here instead would defeat the purpose — each click
       * would arrive with a different key.
       */
      clientRef?: string;
    }) => post<{ succeeded: string[]; failed: { policyId: string; message: string }[] }>('/policies/bulk', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.resource('policies') });
      client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
    },
  });
}

export function usePolicyRenewals(policyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub('policies', policyId ?? '', 'renewals'),
    queryFn: () => getAll<PolicyRenewalDto>(`/policies/${policyId}/renewals`),
    enabled: Boolean(policyId),
  });
}

export function usePolicyInstallments(policyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub('policies', policyId ?? '', 'installments'),
    queryFn: () => getAll<InstallmentDto>(`/policies/${policyId}/installments`),
    enabled: Boolean(policyId),
  });
}

export function usePolicyEndorsements(policyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub('policies', policyId ?? '', 'endorsements'),
    queryFn: () => getAll<EndorsementDto>(`/policies/${policyId}/endorsements`),
    enabled: Boolean(policyId),
  });
}

export function useCreateEndorsement(policyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) => post<EndorsementDto>(`/policies/${policyId}/endorsements`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.resource('policies') }),
  });
}

export function useRecordInstallmentPayment(policyId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ installmentId, ...input }: { installmentId: string } & Record<string, unknown>) =>
      patch<{ policy: PolicyDto; renewed: boolean }>(
        `/policies/${policyId}/installments/${installmentId}`,
        input,
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.resource('policies') });
      client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
      client.invalidateQueries({ queryKey: queryKeys.resource('installments') });
      client.invalidateQueries({ queryKey: queryKeys.resource('today') });
    },
  });
}

export function useUpcomingRenewals(days = 30, limit = 50) {
  return useQuery({
    queryKey: queryKeys.custom('policies', 'upcoming-renewals', { days, limit }),
    queryFn: () => getAll<PolicyDto>('/policies/upcoming-renewals', { days, limit }),
  });
}

/**
 * Just the number, for the sidebar badge.
 *
 * Asks for one row and reads `meta.total`, which the endpoint computes over the whole
 * window regardless of `limit`. The badge previously reused `useUpcomingRenewals(30, 200)`
 * and pulled ~80 KB of fully-joined policy DTOs on every navigation to render a single
 * integer. Same definition as the renewals board, so the two can never disagree.
 */
export function useRenewalCount(days = 30) {
  return useQuery({
    queryKey: queryKeys.custom('policies', 'upcoming-renewals-count', { days }),
    queryFn: async () => {
      const response = await getList<PolicyDto>('/policies/upcoming-renewals', { days, limit: 1 });
      return response.meta.total;
    },
    staleTime: 60_000,
  });
}

/* ── claims ─────────────────────────────────────────────────────────────────── */

export function useClaimTimeline(claimId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub('claims', claimId ?? '', 'timeline'),
    queryFn: () => getAll<ClaimTimelineDto>(`/claims/${claimId}/timeline`),
    enabled: Boolean(claimId),
  });
}

export function useChangeClaimStatus(claimId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { status: string; note?: string; payoutAmount?: string }) =>
      patch<ClaimDto>(`/claims/${claimId}/status`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.resource('claims') });
      client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
    },
  });
}

export function useAddTimelineEntry(claimId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { actionType: string; description: string }) =>
      post<ClaimTimelineDto>(`/claims/${claimId}/timeline`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.resource('claims') }),
  });
}

/* ── documents (shared across all three owners) ─────────────────────────────── */

export type DocumentScope = 'clients' | 'policies' | 'claims' | 'insurers';

export function useDocuments(scope: DocumentScope, ownerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub(scope, ownerId ?? '', 'documents'),
    queryFn: () => getAll<DocumentDto>(`/${scope}/${ownerId}/documents`),
    enabled: Boolean(ownerId),
  });
}

export function useUploadDocument(scope: DocumentScope, ownerId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, fields }: { file: File; fields: Record<string, string> }) =>
      upload<DocumentDto>(`/${scope}/${ownerId}/documents`, file, fields),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.sub(scope, ownerId, 'documents') }),
  });
}

export function useDeleteDocument(scope: DocumentScope, ownerId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => remove(`/${scope}/${ownerId}/documents/${documentId}`),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.sub(scope, ownerId, 'documents') }),
  });
}

/* ── client activities ──────────────────────────────────────────────────────── */

export function useClientActivities(clientId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sub('clients', clientId ?? '', 'activities'),
    queryFn: () => getAll<ActivityDto>(`/clients/${clientId}/activities`),
    enabled: Boolean(clientId),
  });
}

export function useCreateActivity(clientId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) => post<ActivityDto>(`/clients/${clientId}/activities`, input),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.sub('clients', clientId, 'activities') }),
  });
}

/* ── reminders ──────────────────────────────────────────────────────────────── */

export function useCompleteReminder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patch(`/reminders/${id}/complete`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.resource('reminders') });
      client.invalidateQueries({ queryKey: queryKeys.resource('dashboard') });
    },
  });
}

export function useReopenReminder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patch(`/reminders/${id}/reopen`),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.resource('reminders') }),
  });
}

/* ── the daily worklist («Η μέρα μου») ──────────────────────────────────────── */

export function useToday(mine: boolean) {
  return useQuery({
    queryKey: queryKeys.custom('today', 'summary', { mine }),
    queryFn: () => getOne<TodayDto>('/today', { mine }),
    staleTime: 60_000,
  });
}

/* ── collections (cross-policy instalments) ─────────────────────────────────── */

export function useInstallmentsList(params: Record<string, unknown>) {
  return useQuery({
    queryKey: queryKeys.list('installments', params),
    queryFn: () => getList<InstallmentDto>('/installments', params),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

/* ── dashboard ──────────────────────────────────────────────────────────────── */

export function useDashboardSummary(params: DashboardQuery) {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', 'summary', params),
    queryFn: () => getOne<DashboardSummaryDto>('/dashboard/summary', params),
  });
}

export function useMonthlySeries(params: DashboardQuery & { months?: number }) {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', 'premiums-by-month', params),
    queryFn: () => getAll<TimeSeriesPoint>('/dashboard/premiums-by-month', params),
  });
}

export function useBreakdown(
  dimension: 'company' | 'client' | 'insurance-type' | 'agent',
  params: DashboardQuery,
) {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', `by-${dimension}`, params),
    queryFn: () => getAll<BreakdownRow>(`/dashboard/by-${dimension}`, params),
  });
}

export function useAgentPerformance(params: DashboardQuery) {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', 'agent-performance', params),
    queryFn: () => getAll<AgentPerformanceRow>('/dashboard/agent-performance', params),
  });
}

export function useClaimsAgeing() {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', 'claims-ageing'),
    queryFn: () => getAll<{ bucket: string; count: number; amount: string }>('/dashboard/claims-ageing'),
  });
}

export function useDashboardRenewalsList() {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', 'upcoming-renewals'),
    queryFn: () =>
      getAll<{
        id: string;
        policyNumber: string;
        clientName: string;
        endDate: string;
        premiumAmount: string;
        insuranceCompanyName: string;
        daysLeft: number;
        plates?: string[] | null;
      }>('/dashboard/upcoming-renewals'),
  });
}

export function useDashboardOverdueReminders() {
  return useQuery({
    queryKey: queryKeys.custom('dashboard', 'overdue-reminders'),
    queryFn: () =>
      getAll<{
        id: string;
        title: string;
        reminderDate: string;
        priority: string;
        daysOverdue: number;
        relatedEntityLabel: string | null;
        relatedEntityType: string | null;
        relatedEntityId: string | null;
      }>('/dashboard/overdue-reminders'),
  });
}

/* ── commissions ────────────────────────────────────────────────────────────── */

export function useProducerSplits(params: { dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: queryKeys.custom('commissions', 'producer-splits', params),
    queryFn: () =>
      getAll<{
        agentId: string;
        agentName: string;
        sharePercentage: string;
        policyCount: number;
        agencyCommission: string;
        producerShare: string;
      }>('/commissions/producer-splits', params),
  });
}

export function useCloseSettlement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post(`/commissions/settlements/${id}/close`),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.resource('settlements') }),
  });
}

/* ── search & notifications ─────────────────────────────────────────────────── */

export function useGlobalSearch(term: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.custom('search', 'global', term),
    queryFn: () => getOne<SearchResultsDto>('/search', { q: term }),
    enabled: enabled && term.trim().length >= 2,
    staleTime: 15_000,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: queryKeys.custom('notifications', 'list'),
    queryFn: () => getAll<NotificationDto>('/notifications'),
    // Polled rather than pushed: a websocket for a handful of daily notices would be
    // more moving parts than the feature is worth at this scale.
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => post('/notifications/mark-read', { ids }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.resource('notifications') }),
  });
}

/* ── import ─────────────────────────────────────────────────────────────────── */

export function useImportClients() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) =>
      upload<ImportResultDto>('/imports/clients', file, { dryRun: String(dryRun) }),
    onSuccess: (_data, variables) => {
      if (!variables.dryRun) client.invalidateQueries({ queryKey: queryKeys.resource('clients') });
    },
  });
}
